import { inject, Injectable } from '@angular/core';
import {
  $createTextNode,
  $getRoot,
  createEditor,
  LexicalEditor,
  ParagraphNode,
  TextNode
} from 'lexical';
import { $dfs } from '@lexical/utils';
import { registerRichText } from '@lexical/rich-text';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import {
  BehaviorSubject,
  Subject,
  distinctUntilChanged,
  debounceTime,
  switchMap,
  of,
  catchError,
  takeUntil,
  filter,
  tap
} from 'rxjs';
import { MentionNode, MentionPayload } from '../nodes/mention.node';
import {
  registerMentionPlugin,
  MentionPlugin,
  INSERT_MENTION_COMMAND,
  MentionMenuState
} from '../plugins/mention.plugin';
import { MentionUser } from '../components/mention-dropdown/mention-dropdown.component';
import { USER_SEARCH_PROVIDER } from '../user-search.token';

const MENTION_QUERY_DEBOUNCE = 150;

export interface MentionState {
  menuState: MentionMenuState;
  users: MentionUser[];
  loading: boolean;
}

@Injectable()
export class RichtextEditorService {
  private userSearchProvider = inject(USER_SEARCH_PROVIDER, { optional: true });

  private readonly editor = createEditor({
    nodes: [ParagraphNode, TextNode, MentionNode],
    onError: error => console.error('Richtext Editor Error:', error)
  });
  private listeners: (() => void)[] = [];
  private mentionPlugin?: MentionPlugin;
  private valueSubject = new BehaviorSubject<string>('');
  private mentionStateSubject = new BehaviorSubject<MentionState>({
    menuState: 'closed',
    users: [],
    loading: false
  });
  private destroy$ = new Subject<void>();

  value$ = this.valueSubject.pipe(distinctUntilChanged());
  mentionState$ = this.mentionStateSubject.asObservable();

  getEditor(): LexicalEditor {
    return this.editor;
  }

  private isInitialized(): boolean {
    return this.editor.getRootElement() !== null;
  }

  initializeEditor(rootElement: HTMLElement): void {
    this.editor.setRootElement(rootElement);
    this.listeners.push(
      registerRichText(this.editor),
      this.editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() =>
          this.valueSubject.next($generateHtmlFromNodes(this.editor))
        );
      })
    );

    if (this.valueSubject.value) {
      this.setValue(this.valueSubject.value);
    }
  }

  enableMentions(): void {
    if (this.mentionPlugin) return;
    if (!this.userSearchProvider) {
      throw new Error(
        'USER_SEARCH_PROVIDER must be provided when mentions are enabled'
      );
    }

    const userSearchProvider = this.userSearchProvider;
    this.mentionPlugin = registerMentionPlugin(this.editor);

    this.mentionPlugin.state$
      .pipe(
        tap(menuState => this.updateMentionState({ menuState })),
        filter(
          (state): state is Exclude<MentionMenuState, 'closed'> =>
            state !== 'closed'
        ),
        debounceTime(MENTION_QUERY_DEBOUNCE),
        tap(() => this.updateMentionState({ loading: true })),
        switchMap(state =>
          userSearchProvider
            .findAll({ q: state.query })
            .pipe(catchError(() => of({ items: [] as MentionUser[] })))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(response => {
        this.updateMentionState({
          users: response.items || [],
          loading: false
        });
      });
  }

  private updateMentionState(partial: Partial<MentionState>): void {
    this.mentionStateSubject.next({
      ...this.mentionStateSubject.value,
      ...partial
    });
  }

  destroyEditor(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mentionPlugin?.unregister();
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
  }

  setValue(html: string): void {
    const value = html || '';
    this.valueSubject.next(value);

    if (!this.isInitialized()) return;

    this.editor.update(() => {
      const root = $getRoot();
      root.clear();

      if (html?.trim()) {
        const dom = new DOMParser().parseFromString(html, 'text/html');
        root.append(...$generateNodesFromDOM(this.editor, dom));

        // Add spaces around mentions - HTML import strips whitespace
        for (const { node } of $dfs(root)) {
          if (node instanceof MentionNode) {
            node.insertBefore($createTextNode(' '));
            node.insertAfter($createTextNode(' '));
          }
        }
      }
    });
  }

  insertMention(payload: MentionPayload): void {
    this.editor.dispatchCommand(INSERT_MENTION_COMMAND, payload);
  }
}
